import React, { useEffect, useState, useContext, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';

import './styles.scss';

import DataListsProxy, { DataListType, ProgramListMode } from 'api/dataLists/dataListsProxy';
import WorklistProxy from 'api/CoreMeasures/WorklistProxy';
import ExcelJS from 'exceljs';

import PageLayout from 'global_elements/Layouts/PageLayout';
import InlineText from 'global_elements/Text/InlineText';
import Paragraph from 'global_elements/Text/Paragraph';
import Button from 'global_elements/Button';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import LabeledTextInput from 'global_elements/Inputs/TextInput/LabeledTextInput';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';

import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { TextInputVariant } from 'global_elements/Inputs/TextInput/variants';
import { ButtonVariant } from 'global_elements/Button/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { AllRoutes } from 'constants/routes';
import { CoreMeasuresWorklistDataRow, CMPatientCompletionDataRow, IncompletePatientsDataRow, PixSubmittedDataRow, EventsListDataRow } from 'types/tableProps';
import { SingleStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { SelectOption, FacilitySelectOption } from 'types/inputProps';
import { UserContext } from 'context/user';
import { ActiveFacilityContext } from 'context/activeFacility';
import { CoreMeasuresPatientSearchCriteria } from 'interfaces/coreMeasures/coreMeasuresPatientSearchResult';
import Utilities from 'api/lib/Utilities';
import { CoreMeasurePatientSearchState } from 'types/CoreMeasurePatient';
import { LabeledDatePickerInput } from 'global_elements/Inputs/LabeledDatePickerInput';
import CoreMeasuresWorklistTable from 'global_elements/Layouts/DataTables/CoreMeasuresWorklistTable';
import CMPatientCompletionTable from 'global_elements/Layouts/DataTables/CMPatientCompletionTable';
import EventsListTable from 'global_elements/Layouts/DataTables/EventsListTable';
import CollapsibleSection from 'global_elements/Layouts/CollapsibleSection';
import TertiaryHeader from 'global_elements/Text/TertiaryHeader';
import PopupWindow from 'global_elements/Layouts/PopupWindow';
import { ReactComponent as X } from 'icons/mho-x-icon.svg';
import IncompletePatientsTable from 'global_elements/Layouts/DataTables/IncompletePatientsTable';
import PIXSubmittedTable from 'global_elements/Layouts/DataTables/PIXSubmittedTable/PIXSubmittedTable';
import { Roles } from 'constants/roles';
import { saveAs } from 'file-saver';
import MHODateTime from 'domain/dateTime/MHODateTime';
import { EventTypes } from 'constants/events';
import { hasPixAccess } from 'global_elements/Navigation/lib/navOptions';

const currDate = new Date();
const currYear = currDate.getFullYear();
const initialFromDate = new Date(`01/01/${currYear}`); // Initial date range: January 1st of prior year (at least since 2025)
const initialDateType = { value: '2', label: 'Discharge Date' };

const CoreMeasuresWorklistPage = (): React.JSX.Element => {
  const { user, coreMeasuresPatientSearchData, SetCoreMeasuresPatientSearchData } = useContext(UserContext);
  const [searchState, setSearchState] = useState<CoreMeasurePatientSearchState>(
    coreMeasuresPatientSearchData?.query || {
      fromDate: initialFromDate,
      toDate: null,
      dateRangeType: initialDateType,
      sample: null,
      serviceCode: null,
      age: null,
      accountNumber: '',
      medicalRecordNumber: '',
      executed: false,
      facility: undefined,
      currentPage: 1,
      program: null,
      completionType: null,
    },
  );
  const [patientData, setPatientData] = useState<CoreMeasuresWorklistDataRow[]>([]);
  const [completionGridData, setCompletionGridData] = useState<CMPatientCompletionDataRow[]>([]);
  const [pixSubmittedData, setPixSubmittedData] = useState<PixSubmittedDataRow[]>([]);
  const [incompletePatientsData, setIncompletePatientsData] = useState<IncompletePatientsDataRow[]>([]);
  const [eventListData, setEventListData] = useState<EventsListDataRow[]>([]);
  const [selectedIncompleteMonth, setSelectedIncompleteMonth] = useState<string>('');
  const [selectedEventMonth, setSelectedEventMonth] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<EventTypes | null>(null);
  const [availableFacilities, setAvailableFacilities] = useState<SelectOption[]>(coreMeasuresPatientSearchData?.availableFacilities ?? []);
  const { facility } = useContext(ActiveFacilityContext);
  const [sampleOptions, setSampleOptions] = useState<SelectOption[]>([]);
  const [ageRangeOptions, setAgeRangeOptions] = useState<SelectOption[]>([]);
  const [completionOptions, setCompletionOptions] = useState<SelectOption[]>([]);
  const [dateRangeOptions, setDateRangeOptions] = useState<SelectOption[]>([]);
  const [serviceCodeOptions, setServiceCodeOptions] = useState<SelectOption[]>([]);
  const [programOptions, setProgramOptions] = useState<SelectOption[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingInitialWorklist, setIsLoadingInitialWorklist] = useState(true);
  const [showIncompletePatientsWindow, setShowIncompletePatientsWindow] = useState<boolean>(false);
  const [showEventListWindow, setShowEventListWindow] = useState<boolean>(false);
  const [worklistSortField, setWorklistSortField] = useState<string>('');
  const [worklistSortAsc, setWorklistSortAsc] = useState<boolean>(false);
  const [isClearingDropdowns, setIsClearingDropdowns] = useState<boolean>(false);

  const contextFacilityFiltered = facility.value !== '' ? facility : undefined;
  const isAdmin = user?.role === Roles.ADMIN || user?.role === Roles.SUPER_ADMIN;

  const history = useHistory();
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const inputRef = React.useRef<any>(undefined);

  /**
   * Shared function to load the various drop downs for the System Alert Editor.
   *
   * @param optionType The 4 types of options: Platform, Severity, ClientType, and AlertType
   * @returns {Promise} the retrieved options list.
   */
  const loadDropdownOptions = (optionType: DataListType): Promise<SelectOption[]> => new Promise<SelectOption[]>((resolve, reject) => {
    DataListsProxy.getOptionList(
      optionType,
      (response) => {
        const dropdownOptions: SelectOption[] = [];

        if (response.data) {
          response.data.forEach((option) => {
            dropdownOptions.push({
              label: option.lookupDesc,
              value: option.lookupID.toString(),
            });
          });
        }
        resolve(dropdownOptions);
      },
      (error) => {
        console.log(error);
        reject(error);
      },
    );
  });

  const loadProgramOptions = (fid: number, isReload?: boolean): void => {
    setProgramOptions([]);
    // Need to clear out old program value when reloading programs
    if (isReload) {
      setSearchState((prevState) => ({
        ...prevState,
        program: null,
      }));
    }
    DataListsProxy.getPrograms(fid, ProgramListMode.CoreMeasures).then((results) => {
      const options = results.map((x) => ({
        label: x.programName,
        value: x.programID.toString(),
      }));

      setProgramOptions(options);
    });
  };

  const loadServiceCodeOptions = (fid: number, isReload?: boolean): void => {
    setServiceCodeOptions([]);
    // Need to clear out old service code value when reloading service codes
    if (isReload) {
      setSearchState((prevState) => ({
        ...prevState,
        serviceCode: null,
      }));
    }
    WorklistProxy.getServiceCodes(fid, ProgramListMode.CoreMeasures).then((results) => {
      if (results.data) {
        const options = results.data.map((x) => ({
          label: x.unitCode,
          value: x.unitCode,
        }));
        setServiceCodeOptions(options);
      }
    });
  };

  const printWorklist = (): void => {
    setIsPrinting(true);
    // Delay to give print version of worklist time to render
    setTimeout(() => {
      window.print();
      setIsPrinting(false); // Reset isPrinting variable for next time
    }, 500);
  };

  const loadAllDropdownOptions = (): void => {
    loadDropdownOptions(DataListType.SampleType).then((options) => setSampleOptions(options));
    loadDropdownOptions(DataListType.DateRangeType).then((options) => setDateRangeOptions(options));
    loadDropdownOptions(DataListType.AgeRangeType).then((options) => setAgeRangeOptions(options));
    loadDropdownOptions(DataListType.CompletionType).then((options) => setCompletionOptions(options));
    if (searchState.facility?.value) {
      loadProgramOptions(parseInt(searchState.facility.value, 10));
      loadServiceCodeOptions(parseInt(searchState.facility.value, 10));
    }
  };

  const clearAllDropdowns = (): void => {
    setSampleOptions([]);
    setDateRangeOptions([]);
    setAgeRangeOptions([]);
    setServiceCodeOptions([]);
    setProgramOptions([]);
    setCompletionOptions([]);
  };

  const loadCompletionGridData = (fid: number): void => {
    setCompletionGridData([
      {
        month: 'Loading...',
        complete: '',
        total: '',
        completionPercent: '',
        leaveDays: '',
        monthID: -1,
        yearID: -1,
        sdohComplete: '',
        restraintsCnt: '',
        seclusionsCnt: '',
      },
    ]);
    WorklistProxy.GetCompletionGrid(fid).then((response) => {
      if (response.data) {
        const completionData: CMPatientCompletionDataRow[] = [];

        for (let i = 0; i < response.data.length; i += 1) {
          const completionDataRow = response.data[i];

          if (completionDataRow) {
            completionData.push({
              month: completionDataRow.completenessMonth,
              complete: completionDataRow.completeCnt,
              total: completionDataRow.totalCnt,
              completionPercent: completionDataRow.completePercent,
              leaveDays: completionDataRow.leaveDaysPresent,
              monthID: completionDataRow.monthID,
              yearID: completionDataRow.yearID,
              sdohComplete: completionDataRow.sdohComplete,
              restraintsCnt: completionDataRow.restraintsCnt === null ? '0' : completionDataRow.restraintsCnt.toString(),
              seclusionsCnt: completionDataRow.seclusionsCnt === null ? '0' : completionDataRow.seclusionsCnt.toString(),
            } as CMPatientCompletionDataRow);
          }
        }
        setCompletionGridData(completionData);
      }
    });
  };

  const loadSubmittedPixData = (fid: number): void => {
    const prevYear = currYear - 1;

    setPixSubmittedData([
      {
        year: '...',
        Jan: '',
        Feb: '',
        Mar: '',
        Apr: '',
        May: '',
        Jun: '',
        Jul: '',
        Aug: '',
        Sep: '',
        Oct: '',
        Nov: '',
        Dec: '',
        YTD: '',
      },
    ]);

    WorklistProxy.getSubmittedPixCounts(fid).then((response) => {
      if (response.data && response.data.length > 0) {
        let currYearSubmittedPixData: PixSubmittedDataRow = {
          year: currYear.toString(),
          Jan: '',
          Feb: '',
          Mar: '',
          Apr: '',
          May: '',
          Jun: '',
          Jul: '',
          Aug: '',
          Sep: '',
          Oct: '',
          Nov: '',
          Dec: '',
          YTD: '',
        };

        let prevYearSubmittedPixData: PixSubmittedDataRow = {
          year: prevYear.toString(),
          Jan: '',
          Feb: '',
          Mar: '',
          Apr: '',
          May: '',
          Jun: '',
          Jul: '',
          Aug: '',
          Sep: '',
          Oct: '',
          Nov: '',
          Dec: '',
          YTD: '',
        };

        for (let i = 0; i < response.data.length; i += 1) {
          const pixSubmittedDataRow = response.data[i];
          const submittedMonthName = pixSubmittedDataRow.importedMonthName;
          const submittedMonthValue = pixSubmittedDataRow.facilityMonthCount;
          const submittedYear = pixSubmittedDataRow.importedYear;

          if (pixSubmittedDataRow) {
            if (submittedYear === prevYear) {
              prevYearSubmittedPixData = { ...prevYearSubmittedPixData, [submittedMonthName]: submittedMonthValue };
            } else if (submittedYear === currYear) {
              currYearSubmittedPixData = { ...currYearSubmittedPixData, [submittedMonthName]: submittedMonthValue };
            }
          }
        }

        // Suppress 2024 data, since it will all be null
        const pixSubmittedTableData = currYear === 2026 ? [currYearSubmittedPixData] : [prevYearSubmittedPixData, currYearSubmittedPixData];

        setPixSubmittedData(pixSubmittedTableData);
      } else {
        setPixSubmittedData([]);
      }
    });
  };

  const search = (): void => {
    setSearchState((prevState) => ({
      ...prevState,
      executed: true,
    }));
    // ***added dischargeDate 2/2024
    setPatientData([
      {
        patientName: 'Searching...',
        patientID: -1,
        accountNumber: '',
        medicalRecordNumber: '',
        dateAdmitted: '',
        dateDischarged: '',
        age: '',
        serviceCode: '',
        complete: '',
        sample: '',
      } as CoreMeasuresWorklistDataRow,
    ]);

    let adjustedServiceCode: string | null;

    if (searchState.serviceCode?.value && searchState.serviceCode.value !== 'All Service Codes') {
      adjustedServiceCode = searchState.serviceCode.value;
    } else {
      // Need to set service code to null if you want all service codes
      adjustedServiceCode = null;
    }

    const searchCriteria: CoreMeasuresPatientSearchCriteria = {
      FacilityID: searchState.facility ? parseInt(searchState.facility.value, 10) : parseInt(facility.value, 10),
      DateRangeType: searchState.dateRangeType ? parseInt(searchState.dateRangeType.value, 10) : 0,
      DateRangeFrom: searchState.fromDate,
      DateRangeTo: searchState.toDate,
      SampleType: searchState.sample ? parseInt(searchState.sample.value, 10) : 0,
      serviceCode: adjustedServiceCode,
      CompletionType: searchState.completionType ? parseInt(searchState.completionType.value, 10) : 0,
      AgeRangeType: searchState.age ? parseInt(searchState.age.value, 10) : 0,
      PatientNumber: searchState.accountNumber ? searchState.accountNumber.trim() : null,
      MedicalRecordNumber: searchState.medicalRecordNumber ? searchState.medicalRecordNumber.trim() : null,
      ProgramID: searchState.program ? parseInt(searchState.program.value, 10) : null,
    };

    WorklistProxy.Search(searchCriteria)
      .then((response) => {
        if (response.data) {
          const workList: CoreMeasuresWorklistDataRow[] = [];

          for (let i = 0; i < response.data.length; i += 1) {
            const worklistData = response.data[i];

            if (worklistData) {
              workList.push({
                patientID: worklistData.patientID,
                patientName: worklistData.patientName,
                accountNumber: worklistData.patientNumber,
                medicalRecordNumber: worklistData.medicalRecordNumber,
                dateAdmitted: worklistData.dateAdmitted,
                dateDischarged: worklistData.dateDischarged,
                age: worklistData.ageAtAdmission.toString(),
                serviceCode: worklistData.serviceCode,
                complete: worklistData.complete,
                sample: worklistData.sample,
                sdoh: worklistData.sdohComplete ? worklistData.sdohComplete : '',
                canEdit: worklistData.editAllowed === 1,
              } as CoreMeasuresWorklistDataRow);
            }
          }
          setPatientData(workList);
          setIsLoadingInitialWorklist(false);
        } else {
          console.error(response.message);
          setPatientData([
            {
              patientName: response.message,
              patientID: -1,
              accountNumber: '',
              medicalRecordNumber: '',
              dateAdmitted: '',
              dateDischarged: '',
              age: '',
              serviceCode: '',
              complete: '',
              sample: '',
            } as CoreMeasuresWorklistDataRow,
          ]);
        }
      })
      .catch((error) => {
        console.error(error);
        setPatientData([]);
      });
  };

  useEffect(() => {
    if (!isAdmin && contextFacilityFiltered !== searchState.facility) {
      setPatientData([]);
      inputRef.current?.focus();
      setSearchState((prevState) => ({
        ...prevState,
        executed: false,
        facility: contextFacilityFiltered,
      }));
    }
  }, [contextFacilityFiltered]);

  useEffect(() => {
    // Don't reload dropdowns when user returns to page after searching once
    const reloadDropdowns = !searchState.executed;
    if (searchState.facility?.value) {
      if (!searchState.facility.isCoreMeasures) {
        setErrorMessage('Core Measures features are not available for this facility.');
      } else {
        setErrorMessage('');
        loadProgramOptions(parseInt(searchState.facility.value, 10), reloadDropdowns);
        loadServiceCodeOptions(parseInt(searchState.facility.value, 10), reloadDropdowns);

        // Update patient completion grid for new facility
        if (searchState.facility?.isCoreMeasures) {
          loadCompletionGridData(parseInt(searchState.facility.value, 10));
        }

        loadSubmittedPixData(parseInt(searchState.facility.value, 10));
      }
    }
  }, [searchState.facility]);

  useEffect(() => {
    if (searchState.executed) {
      Utilities.setCellTitles();
    }
  }, [searchState]);

  useEffect(() => {
    if (searchState.executed) {
      SetCoreMeasuresPatientSearchData({
        query: searchState,
        data: patientData,
        availableFacilities,
      });
    } else {
      SetCoreMeasuresPatientSearchData(null);
    }
  }, [patientData, searchState]);

  useEffect(() => {
    if (patientData.length > 0) {
      Utilities.setCellTitles();
    }
  }, [patientData]);

  useEffect(() => {
    // Load dropdown options
    loadAllDropdownOptions();

    DataListsProxy.getAllUserFacilities(
      (response) => {
        if (response?.data) {
          const facilityOptions: FacilitySelectOption[] = [];

          response.data
            .sort((a, b) => {
              if (a.facilityName.toLowerCase() < b.facilityName.toLowerCase()) {
                return -1;
              }

              if (a.facilityName.toLowerCase() > b.facilityName.toLowerCase()) {
                return 1;
              }

              return 0;
            })
            .forEach((element) => {
              if (element.facilityName && element.facilityID) {
                facilityOptions.push({
                  label: element.facilityName,
                  value: element.facilityID.toString(),
                  isCoreMeasures: !!element.coreMeasures, // Convert numbers to boolean
                  hasServiceCodes: !!element.svcCode,
                  pixDataEntry: element.pixDataEntry,
                  isUHS: Boolean(element.isUHS && element.isUHS > 0),
                });
              }
            });

          setAvailableFacilities(facilityOptions);
        }
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );

    const selectedFacility = user?.role === Roles.ADMIN || user?.role === Roles.SUPER_ADMIN ? searchState.facility : contextFacilityFiltered;

    // Populate patient grid on page load
    if (selectedFacility && selectedFacility.value !== '0' && selectedFacility.isCoreMeasures) {
      search();
    }
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchState((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
      executed: false,
    }));
  };

  const handleDateRangeSelectChange = (option: SelectOption): void => {
    setSearchState((prevState) => ({
      ...prevState,
      dateRangeType: option,
      executed: false,
    }));
  };

  const handleFacilitySelectChange = (option: FacilitySelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      facility: option,
      executed: false,
    }));
  };

  const handleSampleSelectChange = (option: SelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      sample: option,
      executed: false,
    }));
  };

  const handleServiceCodeSelectChange = (option: SelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      serviceCode: option,
      executed: false,
    }));
  };

  const handleProgramSelectChange = (option: SelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      program: option,
      executed: false,
    }));
  };

  const handleAgeSelectChange = (option: SelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      age: option,
      executed: false,
    }));
  };

  const handleCompletionSelectChange = (option: SelectOption): void => {
    setPatientData([]);
    setSearchState((prevState) => ({
      ...prevState,
      completionType: option,
      executed: false,
    }));
  };

  const handleDatePickerChange = (selectedDate: Date | null, dateType: string): void => {
    // Don't clear the patient list if it's loading for the first time
    if (!isLoadingInitialWorklist) {
      setPatientData([]);
    }
    setSearchState((prevState) => ({
      ...prevState,
      [dateType]: selectedDate,
    }));
  };

  const getPrintLabel = (label: string, valueText: string): any => (
    <p>
      <span style={{ fontWeight: 'bold' }}>
        {label}
:
        {' '}
      </span>
      {' '}
      {valueText}
    </p>
  );

  const goToPatientDetails = (row: CoreMeasuresWorklistDataRow, e: React.UIEvent<Element, UIEvent>): void => {
    e.preventDefault();
    // This will take the user to the patient info page
    history.push(`${AllRoutes.PATIENT_ACCOUNTS}/${row.patientID}`, { coreMeasuresRedirect: 1 });
  };

  // ****clear data
  const clearSearchCriteria = (): void => {
    setIsClearingDropdowns(true);
    setPatientData([]);
    // Clear all dropdown option lists (forces a reload so they can reset)
    clearAllDropdowns();
    setSearchState((prevState) => ({
      ...prevState,
      fromDate: initialFromDate,
      toDate: null,
      dateRangeType: initialDateType,
      sample: null,
      age: null,
      serviceCode: null,
      program: null,
      accountNumber: '',
      medicalRecordNumber: '',
      executed: false,
      completionType: null,
    }));

    // Load dropdown options again
    loadAllDropdownOptions();

    // Slight delay to allow datepickers to reset
    setTimeout(() => {
      setIsClearingDropdowns(false);
    }, 50);
  };

  const invalidDate = (field: 'fromDate' | 'toDate'): string | undefined => {
    const admissionMinDate: Date = new Date(Date.now());
    const admissionMaxDate: Date = new Date(Date.now());
    const dischargeMinDate: Date = new Date(Date.now());
    const dischargeMaxDate: Date = new Date(Date.now());
    const myDate = searchState[field];

    admissionMaxDate.setDate(admissionMinDate.getDate() + 3);
    admissionMinDate.setFullYear(admissionMaxDate.getFullYear() - 30);
    dischargeMaxDate.setDate(admissionMinDate.getDate() + 3);
    dischargeMinDate.setFullYear(admissionMaxDate.getFullYear() - 5);

    if (searchState.dateRangeType?.value === '1') {
      if ((myDate && myDate > admissionMaxDate) || (myDate && myDate < admissionMinDate)) {
        return 'Invalid Admission Date';
      }
    }

    if (searchState.dateRangeType?.value === '2') {
      if ((myDate && myDate > dischargeMaxDate) || (myDate && myDate < dischargeMinDate)) {
        return 'Invalid Discharge Date';
      }
    }

    if (field === 'toDate' && myDate && searchState.fromDate && myDate < searchState.fromDate) {
      return 'To Must Be Later Than From';
    }

    return undefined;
  };

  const disableClearButton = (): boolean => {
    if (
      searchState.sample === null
      && searchState.age === null
      && searchState.completionType === null
      && searchState.serviceCode === null
      && searchState.dateRangeType?.value === initialDateType.value
      && searchState.fromDate?.getTime() === initialFromDate.getTime()
      && searchState.toDate === null
      && searchState.program === null
      && searchState.accountNumber === ''
      && searchState.medicalRecordNumber === ''
      && (!isAdmin || !searchState.facility)
    ) {
      return true;
    }

    return false;
  };

  const disableSearchButton = (): boolean => {
    if (errorMessage || ((facility.value === '' || facility.value === '0') && (!searchState.facility || searchState.facility.value === '0'))) {
      return true;
    }

    return false;
  };

  const onChangePage = (page: number): void => {
    Utilities.setCellTitles();

    setSearchState((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const addPatient = (): void => {
    if (searchState.facility) {
      history.push(`${AllRoutes.PATIENT_REGISTRATION}/${searchState.facility.value}`);
    } else {
      history.push(`${AllRoutes.PATIENT_REGISTRATION}/${facility.value}`);
    }
  };

  const closeIncompletePatientsWindow = (): void => {
    setShowIncompletePatientsWindow(false);
  };

  const closeEventListWindow = (): void => {
    setShowEventListWindow(false);
  };

  const openIncompletePatientsWindow = (month: string, monthID: number, yearID: number): void => {
    setSelectedIncompleteMonth(month);
    setShowIncompletePatientsWindow(true);
    setIncompletePatientsData([{ name: 'Loading...', age: '', admissionDate: '', dischargeDate: '', patientNumber: '', medicalRecordNumber: '' }]);

    if (!searchState.facility) {
      setIncompletePatientsData([{ name: 'Error: no facility found', age: '', admissionDate: '', dischargeDate: '', patientNumber: '', medicalRecordNumber: '' }]);
      return;
    }
    const facilityID = parseInt(searchState.facility?.value, 10);

    WorklistProxy.GetIncompletePatients(facilityID, yearID, monthID).then((response) => {
      if (response.data) {
        const incompletePatients = response.data.map((result) => ({
          name: result.patientName,
          age: result.patientAge.toString(),
          admissionDate: result.dateAdmitted,
          dischargeDate: result.dateDischarged,
          patientNumber: result.patientNumber,
          medicalRecordNumber: result.medicalRecordNbr,
        }));

        setIncompletePatientsData(incompletePatients);
      }
    });
  };

  const openEventsListWindow = (month: string, monthID: number, yearID: number, eventType: EventTypes): void => {
    setSelectedEventMonth(month);
    setSelectedEventType(eventType);
    setShowEventListWindow(true);
    setEventListData([{ name: 'Loading...', patientAccountNumber: '', eventStartDate: '', eventStopDate: '', eventStartTime: '', eventStartTimeUTD: '', eventStopTime: '', eventStopTimeUTD: '' }]);

    if (!searchState.facility) {
      setEventListData([
        { name: 'Error: no facility found', patientAccountNumber: '', eventStartDate: '', eventStopDate: '', eventStartTime: '', eventStartTimeUTD: '', eventStopTime: '', eventStopTimeUTD: '' },
      ]);
      return;
    }
    const facilityID = parseInt(searchState.facility?.value, 10);

    WorklistProxy.GetEventList(facilityID, yearID, monthID, eventType).then((response) => {
      if (response.data) {
        const eventList = response.data.map((result) => ({
          name: result.patientName,
          patientAccountNumber: result.patientNumber,
          eventStartDate: result.eventStartDate,
          eventStopDate: result.eventStopDate,
          eventStartTime: result.eventStartTime,
          eventStartTimeUTD: result.eventStartTimeUTD,
          eventStopTime: result.eventStopTime,
          eventStopTimeUTD: result.eventStopTimeUTD,
        }));

        setEventListData(eventList);
      }
    });
  };

  const exportWorklistToExcel = (): void => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Sample', key: 'sample', width: 10 },
      { header: 'Patient Name', key: 'name', width: 32 },
      { header: 'Account Number', key: 'accountNumber', width: 15 },
      { header: 'Medical Record', key: 'medicalRecord', width: 15 },
      { header: 'Admission Date', key: 'admissionDate', width: 15 },
      { header: 'Discharge Date', key: 'dischargeDate', width: 15 },
      { header: 'Age', key: 'age', width: 15 },
      { header: 'Complete', key: 'complete', width: 15 },
    ];

    for (let i = 0; i < patientData.length; i += 1) {
      worksheet.addRow({
        sample: patientData[i].sample,
        name: patientData[i].patientName,
        accountNumber: patientData[i].accountNumber,
        medicalRecord: patientData[i].medicalRecordNumber,
        admissionDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(patientData[i].dateAdmitted)
          ? new MHODateTime(patientData[i].dateAdmitted).getFormattedCalendarDate()
          : patientData[i].dateAdmitted,
        dischargeDate: new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(patientData[i].dateDischarged)
          ? new MHODateTime(patientData[i].dateDischarged).getFormattedCalendarDate()
          : patientData[i].dateDischarged,
        age: patientData[i].age,
        complete: patientData[i].complete,
      });
    }

    const row = worksheet.getRow(1);
    row.font = { bold: true };

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => saveAs(new Blob([buffer]), `CM_Worklist_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`))
      .catch((err) => console.log('Error writing excel export', err));
  };

  // ***Patient Accounts / Patient Search Page
  return (
    <PageLayout layout={PageLayoutVariant.PADDED} testText="Patient Search Page" pageTitle="Core Measures Work List">
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="core-measures-worklist-content">
        <form onSubmit={search}>
          {isAdmin && availableFacilities.length === 0 && (
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label="Facility"
              options={[{ label: 'Loading...', value: '' }]}
              defaultValue={{ label: 'Loading...', value: '' }}
              value={{ label: 'Loading...', value: '' }}
              onSelection={handleFacilitySelectChange}
            />
          )}
          {isAdmin && availableFacilities.length > 0 && (
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label="Facility"
              options={availableFacilities}
              defaultValue={searchState.facility ?? { label: 'Select a Facility', value: '' }}
              value={searchState.facility}
              onSelection={handleFacilitySelectChange}
            />
          )}
          {!isAdmin && (facility.value === '' || facility.value === '0') && (
            <Paragraph text="Please select a facility in the header to search" fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.EXTRA_LARGE} />
          )}
          {errorMessage ? (
            <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER}>
              <InlineText text={errorMessage} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} />
            </FlexContainer>
          ) : (
            <div>
              <CollapsibleSection expandedOnDefault banner={<TertiaryHeader text="Completion Grid" fontColor={FontColors.PRIMARY} extraClasses="no-print" />} noPrint>
                <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="completion-grid-container">
                  <CMPatientCompletionTable
                    data={completionGridData}
                    openIncompletePatients={(row) => openIncompletePatientsWindow(row.month, row.monthID, row.yearID)}
                    openEventsList={(row, eventType) => openEventsListWindow(row.month, row.monthID, row.yearID, eventType)}
                    hideEvents={Boolean(!searchState.facility || searchState.facility.isUHS)}
                  />
                  {(isAdmin ? hasPixAccess(searchState.facility?.pixDataEntry) : hasPixAccess(contextFacilityFiltered?.pixDataEntry)) && (
                    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START}>
                      <TertiaryHeader text="Submitted PIX Surveys" fontColor={FontColors.PRIMARY} />
                      <PIXSubmittedTable data={pixSubmittedData} />
                    </FlexContainer>
                  )}
                </FlexContainer>
              </CollapsibleSection>
              <CollapsibleSection expandedOnDefault banner={<TertiaryHeader text="Search Filters" fontColor={FontColors.PRIMARY} extraClasses="no-print" />} noPrint>
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses="core-measures-worklist-content_date-range-inputs">
                  {dateRangeOptions.length === 0 && (
                    <LabeledSingleSelect
                      label="Date Type"
                      styles={SingleStandardDropdownStyles}
                      options={[]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.dateRangeType ?? undefined}
                      onSelection={handleDateRangeSelectChange}
                    />
                  )}
                  {dateRangeOptions.length > 0 && (
                    <LabeledSingleSelect
                      label="Date Type"
                      styles={SingleStandardDropdownStyles}
                      options={dateRangeOptions}
                      defaultValue={searchState.dateRangeType}
                      value={searchState.dateRangeType ?? undefined}
                      onSelection={handleDateRangeSelectChange}
                    />
                  )}
                  {searchState.dateRangeType?.value && searchState.dateRangeType.value !== '0' && !isClearingDropdowns && (
                    <LabeledDatePickerInput
                      label="From"
                      name="fromDate"
                      onChange={(selectedDate) => handleDatePickerChange(selectedDate, 'fromDate')}
                      error={invalidDate('fromDate')}
                      testid="from-date-input"
                      initialValue={
                        searchState.fromDate !== null && searchState.fromDate.getTime() !== initialFromDate.getTime()
                          ? searchState.fromDate.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })
                          : initialFromDate.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })
                      }
                    />
                  )}
                  {searchState.dateRangeType?.value && searchState.dateRangeType?.value !== '0' && !isClearingDropdowns && (
                    <LabeledDatePickerInput
                      label="To"
                      name="toDate"
                      onChange={(selectedDate) => handleDatePickerChange(selectedDate, 'toDate')}
                      error={invalidDate('toDate')}
                      testid="to-date-input"
                      initialValue={searchState.toDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                    />
                  )}
                </FlexContainer>
                <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="core-measures-worklist-content__list-inputs">
                  {sampleOptions.length === 0 && (
                    <LabeledSingleSelect
                      label="Sample"
                      styles={SingleStandardDropdownStyles}
                      options={[]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.sample ?? undefined}
                      onSelection={handleSampleSelectChange}
                    />
                  )}
                  {sampleOptions.length > 0 && (
                    <LabeledSingleSelect
                      label="Sample"
                      styles={SingleStandardDropdownStyles}
                      options={sampleOptions}
                      defaultValue={sampleOptions[0]}
                      value={searchState.sample ?? undefined}
                      onSelection={handleSampleSelectChange}
                    />
                  )}
                  {ageRangeOptions.length === 0 && (
                    <LabeledSingleSelect
                      label="Age"
                      styles={SingleStandardDropdownStyles}
                      options={[]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.age ?? undefined}
                      onSelection={handleAgeSelectChange}
                    />
                  )}
                  {ageRangeOptions.length > 0 && (
                    <LabeledSingleSelect
                      label="Age"
                      styles={SingleStandardDropdownStyles}
                      options={ageRangeOptions}
                      defaultValue={ageRangeOptions[0]}
                      value={searchState.age ?? undefined}
                      onSelection={handleAgeSelectChange}
                    />
                  )}
                  {completionOptions.length === 0 && (
                    <LabeledSingleSelect
                      label="Completion Type"
                      styles={SingleStandardDropdownStyles}
                      options={[]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.completionType ?? undefined}
                      onSelection={handleCompletionSelectChange}
                    />
                  )}
                  {completionOptions.length > 0 && (
                    <LabeledSingleSelect
                      label="Completion Type"
                      styles={SingleStandardDropdownStyles}
                      options={completionOptions}
                      defaultValue={completionOptions[0]}
                      value={searchState.completionType ?? undefined}
                      onSelection={handleCompletionSelectChange}
                    />
                  )}
                </FlexContainer>
                <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="core-measures-worklist-content__text-inputs">
                  {searchState.facility?.hasServiceCodes && serviceCodeOptions.length === 0 ? (
                    <LabeledSingleSelect
                      label="Service Code"
                      styles={SingleStandardDropdownStyles}
                      // options={searchState.roleOptions}
                      options={[]}
                      // defaultValue={searchState.roleOptions[0]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.serviceCode ?? undefined}
                      onSelection={handleServiceCodeSelectChange}
                    />
                  ) : (
                    ''
                  )}
                  {searchState.facility?.hasServiceCodes && serviceCodeOptions.length > 0 ? (
                    <LabeledSingleSelect
                      label="Service Code"
                      styles={SingleStandardDropdownStyles}
                      // options={searchState.roleOptions}
                      options={serviceCodeOptions}
                      // defaultValue={searchState.roleOptions[0]}
                      defaultValue={serviceCodeOptions[0]}
                      value={searchState.serviceCode ?? undefined}
                      onSelection={handleServiceCodeSelectChange}
                    />
                  ) : (
                    ''
                  )}
                  {!searchState.facility?.hasServiceCodes && programOptions.length === 0 ? (
                    <LabeledSingleSelect
                      label="Program"
                      styles={SingleStandardDropdownStyles}
                      // options={searchState.roleOptions}
                      options={[]}
                      defaultValue={{ label: 'Loading...', value: '0' }}
                      value={searchState.program ?? undefined}
                      onSelection={handleProgramSelectChange}
                    />
                  ) : (
                    ''
                  )}
                  {!searchState.facility?.hasServiceCodes && programOptions.length > 0 ? (
                    <LabeledSingleSelect
                      label="Program"
                      styles={SingleStandardDropdownStyles}
                      // options={searchState.roleOptions}
                      options={programOptions}
                      defaultValue={programOptions[0]}
                      value={searchState.program ?? undefined}
                      onSelection={handleProgramSelectChange}
                    />
                  ) : (
                    ''
                  )}
                  <LabeledTextInput
                    label="Account Number"
                    placeholder="Search By Account Number..."
                    name="accountNumber"
                    value={searchState.accountNumber}
                    onChange={handleInputChange}
                    type="text"
                    variant={TextInputVariant.PRIMARY}
                  />
                  <LabeledTextInput
                    label="Medical Record"
                    placeholder="Search By Medical Record..."
                    name="medicalRecordNumber"
                    value={searchState.medicalRecordNumber}
                    onChange={handleInputChange}
                    type="text"
                    variant={TextInputVariant.PRIMARY}
                  />
                </FlexContainer>
              </CollapsibleSection>
              <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="filter-print-labels print">
                {searchState.facility && getPrintLabel('Facility', searchState.facility.label)}
                {searchState.dateRangeType && getPrintLabel('Filter Date Type', searchState.dateRangeType.label)}
                {searchState.fromDate && getPrintLabel('From', searchState.fromDate.toDateString())}
                {searchState.toDate && getPrintLabel('To', searchState.toDate.toDateString())}
                {searchState.sample && getPrintLabel('Sample', searchState.sample.label)}
                {searchState.age && getPrintLabel('Age', searchState.age.label)}
                {searchState.completionType && getPrintLabel('Completion Type', searchState.completionType.label)}
                {searchState.program && getPrintLabel('Program', searchState.program.label)}
                {searchState.serviceCode && getPrintLabel('Service Code', searchState.serviceCode.label)}
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.END} align={AlignVariant.END} extraClasses="button-container__search">
                <Button variant={ButtonVariant.SECONDARY} onClick={clearSearchCriteria} disabled={disableClearButton()} extraClasses="no-print">
                  <InlineText text="Clear" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                </Button>
                <Button variant={ButtonVariant.PRIMARY} onClick={() => exportWorklistToExcel()} disabled={!searchState.executed} extraClasses="no-print">
                  <InlineText text="Export to Excel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                </Button>
                <Button variant={ButtonVariant.PRIMARY} onClick={() => printWorklist()} disabled={!searchState.executed} extraClasses="no-print">
                  <InlineText text="Print" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                </Button>
                <Button variant={ButtonVariant.PRIMARY} onClick={search} disabled={disableSearchButton()} submit extraClasses="no-print">
                  <InlineText text="Search" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                </Button>
              </FlexContainer>
            </div>
          )}
        </form>
        {(patientData.length > 0 || searchState.executed) && (
          <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="card-primary">
            <CoreMeasuresWorklistTable
              data={patientData}
              onRowClicked={goToPatientDetails}
              onChangePage={onChangePage}
              defaultPage={searchState.currentPage}
              setSortFieldId={setWorklistSortField}
              setSortFieldAsc={setWorklistSortAsc}
              isPrint={false}
              hasServiceCodes={Boolean(searchState.facility?.hasServiceCodes)}
            />
            {isPrinting && (
              <CoreMeasuresWorklistTable
                data={patientData}
                onRowClicked={goToPatientDetails}
                onChangePage={onChangePage}
                defaultPage={searchState.currentPage}
                defaultSortFieldID={worklistSortField}
                defaultSortAsc={worklistSortAsc}
                setSortFieldId={setWorklistSortField}
                setSortFieldAsc={setWorklistSortAsc}
                isPrint
                hasServiceCodes={Boolean(searchState.facility?.hasServiceCodes)}
              />
            )}
          </FlexContainer>
        )}
        {Boolean(searchState.facility?.isCoreMeasures) && (
          <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="button-container__add-account">
            <Button variant={ButtonVariant.PRIMARY} onClick={addPatient} disabled={!searchState.executed}>
              <InlineText text="Add Patient" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold extraClasses="no-print" />
            </Button>
          </FlexContainer>
        )}

        {showIncompletePatientsWindow && (
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="popup-window-wrapper incomplete-patients-popup-wrapper">
            <PopupWindow>
              <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="popup-window-content">
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="popup-window-header">
                  <TertiaryHeader text={`Incomplete Patients for ${selectedIncompleteMonth}`} fontColor={FontColors.PRIMARY} />
                  <Button
                    variant={ButtonVariant.INVISIBLE}
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      closeIncompletePatientsWindow();
                    }}
                  >
                    <X />
                  </Button>
                </FlexContainer>
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START}>
                  <IncompletePatientsTable data={incompletePatientsData} />
                </FlexContainer>
              </FlexContainer>
            </PopupWindow>
          </FlexContainer>
        )}
        {showEventListWindow && (
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="popup-window-wrapper event-list-popup-wrapper">
            <PopupWindow>
              <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="popup-window-content">
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="popup-window-header">
                  {/* TODO: Get restraint/seclusion text dynamically */}
                  <TertiaryHeader text={`Facility ${selectedEventType === EventTypes.RESTRAINT ? 'Restraints' : 'Seclusions'} Reported During ${selectedEventMonth}`} fontColor={FontColors.PRIMARY} />
                  <Button
                    variant={ButtonVariant.INVISIBLE}
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      closeEventListWindow();
                    }}
                  >
                    <X />
                  </Button>
                </FlexContainer>
                <p className="text --high-priority --bold">
                  **Please note that HBIPS-2 (Rate of Physical Restraint) and HBIPS-3 (Rate of Seclusion) are
                  {' '}
                  <span className="text --bold --underlined">not</span>
                  {' '}
sampled measures.
                </p>
                <p className="text --high-priority">100% of all seclusions and restraints that took place during the month must be reported.</p>

                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START}>
                  <EventsListTable data={eventListData} />
                </FlexContainer>
              </FlexContainer>
            </PopupWindow>
          </FlexContainer>
        )}
      </FlexContainer>
    </PageLayout>
  );
};

export default CoreMeasuresWorklistPage;
